/**
 * Stripe Payment Integration for Good Days Calendar Manager
 * 
 * This module handles all Stripe payment processing on the frontend,
 * including Payment Element creation, payment processing, and error handling.
 * 
 * @package GoodDaysCalendarManager
 * @since 2.4.0
 */

(function() {
    'use strict';

    // Check if Stripe and gdcmData are available
    if (typeof Stripe === 'undefined' || typeof gdcmData === 'undefined') {
        return;
    }

    // Check if payments are enabled (either general payments or table booking payment)
    const generalPaymentsEnabled = gdcmData.payment && gdcmData.payment.enabled;
    const tableBookingPaymentRequired = gdcmData.tableBooking && gdcmData.tableBooking.paymentRequired;
    
    if (!generalPaymentsEnabled && !tableBookingPaymentRequired) {
        return;
    }

    // Check if Stripe is the provider and we have a publishable key
    if (!gdcmData.payment || gdcmData.payment.provider !== 'stripe' || !gdcmData.payment.stripePublishableKey) {
        return;
    }

    /**
     * GDCM Stripe Payment Handler
     */
    window.GDCMStripePayment = {
        stripe: null,
        elements: null,
        paymentElement: null,
        clientSecret: null,
        paymentIntentId: null,
        
        /**
         * Initialize Stripe
         */
        init: function() {
            try {
                this.stripe = Stripe(gdcmData.payment.stripePublishableKey, {
                    locale: 'auto'
                });
            } catch (error) {
            }
        },

        /**
         * Get currency symbol
         * @param {string} currency - Currency code
         * @returns {string} Currency symbol
         */
        getCurrencySymbol: function(currency) {
            const symbols = {
                'USD': '$',
                'EUR': '€',
                'GBP': '£',
                'CAD': 'C$',
                'AUD': 'A$'
            };
            return symbols[currency.toUpperCase()] || currency.toUpperCase() + ' ';
        },

        /**
         * Format price for display
         * @param {number} amount - Amount in currency units
         * @returns {string} Formatted price string
         */
        formatPrice: function(amount) {
            const currency = gdcmData.payment.currency || 'USD';
            const symbol = this.getCurrencySymbol(currency);
            return symbol + parseFloat(amount).toFixed(2);
        },

        /**
         * Create a payment intent and mount the payment element
         * @param {Object} options - Payment options
         * @param {number} options.amount - Amount in currency units
         * @param {string} options.bookingType - 'class' or 'party'
         * @param {number} options.eventId - Event ID
         * @param {string} options.customerEmail - Customer email
         * @param {string} options.customerName - Customer name
         * @param {HTMLElement} options.containerElement - Element to mount payment form
         * @returns {Promise<boolean>} Success status
         */
        createPaymentIntent: async function(options) {
            if (!this.stripe) {
                return false;
            }

            try {
                
                // Build request body based on booking type
                const requestBody = {
                    amount: options.amount,
                    booking_type: options.bookingType,
                    customer_email: options.customerEmail,
                    customer_name: options.customerName
                };
                
                // Add class/party specific fields
                if (options.bookingType === 'class' || options.bookingType === 'party') {
                    requestBody.event_id = options.eventId;
                    requestBody.child_name = options.childName || '';
                    requestBody.party_size = options.partySize || 0;
                }
                
                // Add table booking specific fields
                if (options.bookingType === 'table_booking') {
                    requestBody.num_guests = options.num_guests || 0;
                    requestBody.num_children = options.num_children || 0;
                    requestBody.num_high_chairs = options.num_high_chairs || 0;
                    requestBody.booking_date = options.booking_date || '';
                    requestBody.time_slot = options.time_slot || '';
                    requestBody.table_ids = options.table_ids || '';
                }
                
                // Create payment intent via our REST API
                const response = await fetch(gdcmData.root + 'payment/create-intent', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': gdcmData.nonce
                    },
                    body: JSON.stringify(requestBody)
                });

                const data = await response.json();

                if (!data.success) {
                    throw new Error(data.message || 'Failed to create payment intent');
                }

                this.clientSecret = data.client_secret;
                this.paymentIntentId = data.payment_intent_id;

                // Create Elements instance
                const appearance = {
                    theme: 'stripe',
                    variables: {
                        colorPrimary: '#4f46e5',
                        colorBackground: '#ffffff',
                        colorText: '#1f2937',
                        colorDanger: '#dc2626',
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '8px'
                    },
                    rules: {
                        '.Label': {
                            fontWeight: '500',
                            fontSize: '14px',
                            marginBottom: '8px'
                        },
                        '.Input': {
                            padding: '12px',
                            fontSize: '16px'
                        }
                    }
                };

                this.elements = this.stripe.elements({
                    clientSecret: this.clientSecret,
                    appearance: appearance
                });

                // Create and mount the Payment Element
                this.paymentElement = this.elements.create('payment', {
                    layout: {
                        type: 'tabs',
                        defaultCollapsed: false
                    },
                    fields: {
                        billingDetails: {
                            email: 'never' // We already have the email
                        }
                    }
                });

                if (options.containerElement) {
                    this.paymentElement.mount(options.containerElement);
                }

                return true;
            } catch (error) {
                throw error;
            }
        },

        /**
         * Process the payment
         * @param {Object} bookingData - Complete booking data to save after payment
         * @returns {Promise<Object>} Payment result
         */
        processPayment: async function(bookingData) {
            if (!this.stripe || !this.elements) {
                throw new Error('Payment not initialized');
            }

            try {
                // Get customer email from booking data
                const customerEmail = bookingData.customer_email || bookingData.parent_email || '';
                const customerName = bookingData.customer_name || bookingData.parent_name || '';
                
                // Confirm the payment
                const { error, paymentIntent } = await this.stripe.confirmPayment({
                    elements: this.elements,
                    confirmParams: {
                        return_url: window.location.href, // Fallback, not normally used
                        receipt_email: customerEmail,
                        payment_method_data: {
                            billing_details: {
                                name: customerName,
                                email: customerEmail
                            }
                        }
                    },
                    redirect: 'if_required' // Only redirect if necessary (e.g., 3D Secure)
                });

                if (error) {
                    // Handle different error types
                    if (error.type === 'card_error' || error.type === 'validation_error') {
                        throw new Error(error.message);
                    } else {
                        console.error('Stripe payment error:', error);
                        throw new Error(error.message || 'An unexpected error occurred during payment processing.');
                    }
                }

                if (paymentIntent.status === 'succeeded') {
                    // Payment succeeded, create the booking
                    const confirmResponse = await fetch(gdcmData.root + 'payment/confirm', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': gdcmData.nonce
                        },
                        body: JSON.stringify({
                            payment_intent_id: this.paymentIntentId,
                            booking_data: bookingData
                        })
                    });

                    const confirmData = await confirmResponse.json();

                    if (!confirmData.success) {
                        throw new Error(confirmData.message || 'Failed to confirm booking');
                    }

                    return {
                        success: true,
                        bookingId: confirmData.data.booking_id,
                        paymentIntentId: this.paymentIntentId,
                        amountPaid: confirmData.data.amount_paid,
                        message: confirmData.message
                    };
                } else if (paymentIntent.status === 'processing') {
                    return {
                        success: true,
                        pending: true,
                        message: 'Your payment is being processed. You will receive a confirmation email shortly.'
                    };
                } else {
                    throw new Error('Payment was not successful. Please try again.');
                }
            } catch (error) {
                throw error;
            }
        },

        /**
         * Reset the payment form
         */
        reset: function() {
            if (this.paymentElement) {
                this.paymentElement.destroy();
                this.paymentElement = null;
            }
            this.elements = null;
            this.clientSecret = null;
            this.paymentIntentId = null;
        },

        /**
         * Create the payment form HTML
         * @param {number} amount - Payment amount
         * @returns {string} HTML string
         */
        getPaymentFormHTML: function(amount) {
            const currency = gdcmData.payment.currency || 'USD';
            const formattedPrice = this.formatPrice(amount);
            const testMode = gdcmData.payment.testMode;

            return `
                <div class="gdcm-payment-section tw-mt-6 tw-border-t tw-border-gray-200 tw-pt-6">
                    ${testMode ? `
                        <div class="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-3 tw-mb-4">
                            <p class="tw-text-sm tw-text-orange-800">
                                🧪 <strong>Test Mode:</strong> Use card 4242 4242 4242 4242 with any future date and CVC.
                            </p>
                        </div>
                    ` : ''}
                    
                    <div class="tw-mb-4">
                        <h4 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-flex tw-items-center">
                            <svg class="tw-w-5 tw-h-5 tw-mr-2 tw-text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                            </svg>
                            Secure Payment
                        </h4>
                        <p class="tw-text-sm tw-text-gray-600 tw-mt-1">
                            A booking deposit of <strong class="tw-text-green-600">${formattedPrice}</strong> is required to confirm your reservation.
                        </p>
                    </div>

                    <div id="gdcm-payment-element" class="tw-mb-4">
                        <!-- Stripe Payment Element will be mounted here -->
                        <div class="tw-animate-pulse tw-bg-gray-100 tw-rounded-lg tw-h-32 tw-flex tw-items-center tw-justify-center">
                            <p class="tw-text-gray-500">Loading payment form...</p>
                        </div>
                    </div>

                    <div id="gdcm-payment-message" class="tw-hidden tw-bg-red-50 tw-border tw-border-red-200 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded-lg tw-mb-4">
                    </div>

                    <div class="tw-flex tw-items-center tw-text-xs tw-text-gray-500 tw-mt-4">
                        <svg class="tw-w-4 tw-h-4 tw-mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" fill="#635BFF"/>
                            <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Payments secured by Stripe. Your card details are never stored on our servers.
                    </div>
                </div>
            `;
        },

        /**
         * Show payment error message
         * @param {string} message - Error message
         */
        showError: function(message) {
            const messageEl = document.getElementById('gdcm-payment-message');
            if (messageEl) {
                messageEl.textContent = message;
                messageEl.classList.remove('tw-hidden');
                messageEl.classList.add('tw-bg-red-50', 'tw-border-red-200', 'tw-text-red-700');
            }
        },

        /**
         * Show payment success message
         * @param {string} message - Success message
         */
        showSuccess: function(message) {
            const messageEl = document.getElementById('gdcm-payment-message');
            if (messageEl) {
                messageEl.textContent = message;
                messageEl.classList.remove('tw-hidden', 'tw-bg-red-50', 'tw-border-red-200', 'tw-text-red-700');
                messageEl.classList.add('tw-bg-green-50', 'tw-border-green-200', 'tw-text-green-700');
            }
        },

        /**
         * Hide payment message
         */
        hideMessage: function() {
            const messageEl = document.getElementById('gdcm-payment-message');
            if (messageEl) {
                messageEl.classList.add('tw-hidden');
            }
        }
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            window.GDCMStripePayment.init();
        });
    } else {
        window.GDCMStripePayment.init();
    }

})();
